// Lib
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { buildAppRegisterOverlayUrl } from '../../../auth/modals/register/registrationUtils';

// Actions
import { navigateToUrl } from '../../../reducers/navigationActions';

// Selectors
import { getMilanoteApplicationModeSelector } from '../../../platform/platformSelector';
import {
    getShowMandatoryCookieBanner,
    isOneTrustInitialised as isOneTrustInitialisedSelector,
} from '../../../app/oneTrust/oneTrustSelectors';

// Components
import Toast from '../../../structural/toast/Toast';

// Constants
import { REGISTRATION_ENTRY_POINT } from '../../../../common/users/userConstants';
import { MilanoteApplicationMode } from '../../../../common/platform/platformTypes';

interface DesktopGuestFullBoardPermissionHandlerProps {
    appRegisterUrl: string;
}

/**
 * Doesn't render anything, but handles the redirection to the registration form.
 */
const DesktopGuestFullBoardPermissionHandler: React.FC<DesktopGuestFullBoardPermissionHandlerProps> = (props) => {
    const { appRegisterUrl } = props;

    const isOneTrustInitialised = useSelector(isOneTrustInitialisedSelector);
    const showMandatoryCookieBanner = useSelector(getShowMandatoryCookieBanner);

    const dispatch = useDispatch();
    const dispatchReplaceUrl = useCallback((url: string) => dispatch(navigateToUrl(url)), [dispatch]);

    React.useEffect(() => {
        if (isOneTrustInitialised && !showMandatoryCookieBanner) {
            setTimeout(() => dispatchReplaceUrl(appRegisterUrl), 1000);
        }
    }, [isOneTrustInitialised, showMandatoryCookieBanner]);

    return null;
};

const MobileGuestFullBoardPermissionToast: React.FC<DesktopGuestFullBoardPermissionHandlerProps> = ({
    appRegisterUrl,
}) => {
    const dispatch = useDispatch();
    const navigateToRegister = useCallback(() => {
        dispatch(navigateToUrl(appRegisterUrl));
    }, [appRegisterUrl]);

    return (
        <Toast className="BoardPermissionToast MobileGuestFullBoardPermissionToast edit" onClick={navigateToRegister}>
            <span>Sign up to edit</span>
        </Toast>
    );
};

interface GuestFullBoardPermissionToastProps {
    currentBoardId: string;
    urlPermissionId: string;
    locationQuery: Record<string, string>;
}

/**
 * Determines how to handle arriving as a guest on an editable link board
 * depending on the application mode.
 */
const GuestFullBoardPermissionToast: React.FC<GuestFullBoardPermissionToastProps> = (props) => {
    const { currentBoardId, urlPermissionId, locationQuery } = props;

    const appMode = useSelector(getMilanoteApplicationModeSelector);

    const appRegisterUrl = buildAppRegisterOverlayUrl({
        currentBoardId,
        locationQuery,
        permissionId: urlPermissionId,
        registrationEntryPoint: REGISTRATION_ENTRY_POINT.PUBLIC_EDIT_REGISTRATION,
    });

    return appMode === MilanoteApplicationMode.mobile ? (
        <MobileGuestFullBoardPermissionToast appRegisterUrl={appRegisterUrl} />
    ) : (
        <DesktopGuestFullBoardPermissionHandler appRegisterUrl={appRegisterUrl} />
    );
};

export default GuestFullBoardPermissionToast;
